import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ScanLine, CalendarDays, AlertCircle, FileText } from "lucide-react";
import { formatDate } from "@/lib/format";

const STATUS_COLOR = {
    upcoming: "text-[#00e676]",
    past: "text-[#a3a3a3]",
    draft: "text-[#ffea00]",
    pending_approval: "text-[#ff9500]",
    declined: "text-[#ff3333]",
};
const STATUS_LABEL = {
    upcoming: "Upcoming",
    past: "Past",
    draft: "Draft",
    pending_approval: "Pending Artist",
    declined: "Declined",
};

export default function VenueEvents() {
    const { venue, role } = useOutletContext();
    const [tab, setTab] = useState("upcoming");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        endpoints.venueEvents(venue.id).then(setData).finally(() => setLoading(false));
    };
    useEffect(load, [venue.id]);

    const del = async (eid) => {
        if (!window.confirm("Delete this venue-created event?")) return;
        try { await endpoints.venueDeleteEvent(venue.id, eid); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const saveAsTemplate = async (eid) => {
        try {
            const r = await endpoints.venueSaveEventAsTemplate(venue.id, eid);
            toast.success(`Template saved: ${r.template.name}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const groups = data?.grouped || {};
    const activeList = groups[tab] || [];

    return (
        <div className="pm-fade" data-testid="venue-events-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Events</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Events at {venue.name}</h1>
                </div>
                {role === "manager" && (
                    <div className="flex gap-2">
                        <Link to={`/venue/${venue.id}/templates`} className="pm-btn" data-testid="venue-events-templates-btn">
                            <FileText className="w-4 h-4 mr-2" /> Templates
                        </Link>
                        <Link to={`/venue/${venue.id}/events/new`} className="pm-btn pm-btn-primary" data-testid="venue-create-event-btn">
                            <Plus className="w-4 h-4 mr-2" /> Create Event
                        </Link>
                    </div>
                )}
            </header>

            <div className="px-4 md:px-8 border-b border-[#2a2a2a] flex overflow-x-auto">
                {["upcoming", "past", "draft", "pending_approval", "declined"].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`px-4 py-3 text-[10px] font-mono uppercase whitespace-nowrap border-b-2 ${tab === t ? "border-[#ff4400] text-[#ff4400]" : "border-transparent text-[#a3a3a3]"}`}
                        data-testid={`venue-tab-${t}`}>
                        {STATUS_LABEL[t]} ({(groups[t] || []).length})
                    </button>
                ))}
            </div>

            <div className="px-4 md:px-8 py-6">
                {loading ? <div className="font-mono text-xs text-[#666]">Loading…</div>
                : activeList.length === 0 ? (
                    <div className="border border-dashed border-[#2a2a2a] p-8 text-center" data-testid="venue-events-empty">
                        <CalendarDays className="w-8 h-8 text-[#666] mx-auto" />
                        <div className="mt-3 text-sm text-[#a3a3a3]">No {STATUS_LABEL[tab].toLowerCase()} events.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activeList.map((e) => (
                            <div key={e.id} className="border border-[#2a2a2a] p-4" data-testid={`venue-event-${e.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className={`text-[10px] font-mono uppercase ${STATUS_COLOR[e.status] || "text-[#a3a3a3]"}`}>{STATUS_LABEL[e.status]}</div>
                                        <div className="font-display font-bold uppercase text-lg truncate">{e.name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.artist_name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666] mt-1">{e.registrations}/{e.capacity} registered</div>
                                        {e.source === "venue" && (
                                            <div className="text-[10px] font-mono uppercase text-[#ff9500] mt-1">Venue-created</div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1 items-end">
                                        {(e.status === "upcoming") && (
                                            <Link to={`/venue/${venue.id}/check-in/${e.id}`} className="pm-btn text-[10px]" data-testid={`venue-checkin-${e.id}`}>
                                                <ScanLine className="w-3 h-3 mr-1" /> Check-in
                                            </Link>
                                        )}
                                        {role === "manager" && e.source === "venue" && ["draft", "pending_approval"].includes(e.status) && (
                                            <Link to={`/venue/${venue.id}/events/${e.id}/edit`} className="pm-btn text-[10px]" data-testid={`venue-edit-${e.id}`}>Edit</Link>
                                        )}
                                        {role === "manager" && e.source === "venue" && (
                                            <button onClick={() => saveAsTemplate(e.id)} className="pm-btn text-[10px]" data-testid={`venue-save-as-template-${e.id}`} title="Save this event setup as a reusable template">
                                                <FileText className="w-3 h-3 mr-1" /> Save as template
                                            </button>
                                        )}
                                        {role === "manager" && e.source === "venue" && ["draft", "pending_approval", "declined"].includes(e.status) && (
                                            <button onClick={() => del(e.id)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`venue-delete-${e.id}`}>
                                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {e.status === "pending_approval" && (
                                    <div className="mt-2 text-[10px] font-mono uppercase text-[#ff9500] flex items-center gap-1 border-t border-[#2a2a2a] pt-2">
                                        <AlertCircle className="w-3 h-3" /> Waiting for {e.artist_name} to accept
                                    </div>
                                )}
                                {e.status === "declined" && (
                                    <div className="mt-2 text-[10px] font-mono uppercase text-[#ff3333] border-t border-[#2a2a2a] pt-2">Declined by artist</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
